import { Download, X, CircleAlert } from 'lucide-react';
import { openExternalUrl } from '@/utils/url-utils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FileIconTile } from '@/pages/workflow/component/file-icon-tile';
import {
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { FileUploadResponse } from '@/services/workflow/upload-file';
import { UploadItem } from '@/stores/use-upload-store';

export interface ParsedUploadedFile {
  filename: string;
  fileSize: number;
  downloadUrl?: string;
}

export const parseUploadedFile = (
  value: unknown,
): ParsedUploadedFile | null => {
  if (!value || typeof value !== 'object' || !('success' in value)) return null;
  const response = value as FileUploadResponse;
  if (!response.file?.filename) return null;
  return {
    filename: response.file.filename,
    fileSize: response.file.file_size ?? 0,
    downloadUrl: response.download_url,
  };
};

export const formatUploadBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

interface UploadBarProps {
  percent: number;
  indeterminate?: boolean;
  className?: string;
}

export const UploadBar = ({
  percent,
  indeterminate,
  className,
}: UploadBarProps) => (
  <div
    className={cn(
      'h-0.5 w-full overflow-hidden rounded-full bg-primary/15',
      className,
    )}
  >
    {indeterminate ? (
      <div className='animate-progress-indeterminate h-full w-2/5 rounded-full bg-primary' />
    ) : (
      <div
        className='h-full rounded-full bg-primary transition-[width] duration-300 ease-out'
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    )}
  </div>
);

interface PanelUploadFieldProps {
  upload: UploadItem;
  onCancel: () => void;
  onDismiss: () => void;
}

export const PanelUploadField = ({
  upload,
  onCancel,
  onDismiss,
}: PanelUploadFieldProps) => {
  const isFailed = upload.phase === 'failed';
  const isProcessing = upload.phase === 'processing';

  return (
    <div className='space-y-2'>
      <div
        className={cn(
          'rounded-lg border bg-muted-background p-3',
          isFailed && 'border-destructive/30 bg-destructive/5',
        )}
      >
        <div className='flex items-center gap-2.5'>
          {isFailed ? (
            <span className='flex size-9 shrink-0 items-center justify-center rounded-lg bg-destructive/15 text-destructive'>
              <CircleAlert className='size-4' />
            </span>
          ) : (
            <FileIconTile
              icon={getFileIcon(upload.filename)}
              backgroundColor={getFileIconBackgroundColor(upload.filename)}
              size='small'
              iconColor='#ffffff'
            />
          )}
          <div className='min-w-0 flex-1'>
            <div
              className='truncate text-sm font-semibold'
              title={upload.filename}
            >
              {upload.filename}
            </div>
            {isFailed ? (
              <div className='mt-0.5 truncate text-xs text-destructive'>
                {upload.error || 'Upload failed'}
              </div>
            ) : (
              <div className='mt-0.5 font-mono text-[11px] text-muted-foreground'>
                {isProcessing
                  ? 'Processing on the server…'
                  : `${formatUploadBytes(upload.loaded)} of ${formatUploadBytes(upload.size)}`}
              </div>
            )}
          </div>
          {isFailed ? (
            <Button
              variant='ghost'
              size='icon'
              className='size-7 shrink-0 text-muted-foreground'
              title='Dismiss'
              onClick={onDismiss}
            >
              <X className='size-4' />
            </Button>
          ) : (
            <Button
              variant='ghost'
              size='icon'
              className='size-7 shrink-0 text-muted-foreground'
              title='Cancel upload'
              onClick={onCancel}
            >
              <X className='size-4' />
            </Button>
          )}
        </div>
        {!isFailed && (
          <div className='mt-3 flex items-center gap-2.5'>
            <UploadBar
              percent={upload.percent}
              indeterminate={isProcessing || upload.percent === 0}
              className='flex-1'
            />
            <span className='font-mono text-[11px] font-semibold tabular-nums text-primary'>
              {isProcessing
                ? 'Finishing'
                : upload.percent === 0
                  ? 'Starting'
                  : `${upload.percent}%`}
            </span>
          </div>
        )}
      </div>
      {!isFailed && (
        <p className='px-0.5 text-[11.5px] leading-snug text-muted-foreground'>
          Uploading in the background. You can keep editing the workflow.
        </p>
      )}
    </div>
  );
};

interface PanelUploadSuccessProps {
  file: ParsedUploadedFile;
  canRemove: boolean;
  onRemove: () => void;
}

export const PanelUploadSuccess = ({
  file,
  canRemove,
  onRemove,
}: PanelUploadSuccessProps) => (
  <div className='flex items-center gap-2.5 rounded-lg border bg-muted-background p-2.5'>
    <FileIconTile
      icon={getFileIcon(file.filename)}
      backgroundColor={getFileIconBackgroundColor(file.filename)}
      size='small'
      iconColor='#ffffff'
    />
    <div className='min-w-0 flex-1'>
      <div className='truncate text-sm font-semibold' title={file.filename}>
        {file.filename}
      </div>
      <div className='mt-0.5 font-mono text-[11px] text-muted-foreground'>
        {formatUploadBytes(file.fileSize)} · uploaded
      </div>
    </div>
    {file.downloadUrl && (
      <Button
        variant='ghost'
        size='icon'
        className='size-7 shrink-0 text-muted-foreground'
        title='Download'
        onClick={() => openExternalUrl(file.downloadUrl)}
      >
        <Download className='size-4' />
      </Button>
    )}
    {canRemove && (
      <Button
        variant='ghost'
        size='icon'
        className='size-7 shrink-0 text-muted-foreground'
        title='Remove'
        onClick={onRemove}
      >
        <X className='size-4' />
      </Button>
    )}
  </div>
);
